import { Layout } from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { detectBrand } from '@/lib/brand';
import { hasAnyModule, hasModule } from '@/lib/modules';
import { AdminExcludedPage } from '@/pages/AdminExcluded';
import { AdminRecipientsPage } from '@/pages/AdminRecipients';
import { AdminUsersPage } from '@/pages/AdminUsers';
import { AnalyticsPage } from '@/pages/Analytics';
import { AutomatizacePage } from '@/pages/Automatizace';
import { AutomatizaceObjednavekPage } from '@/pages/AutomatizaceObjednavek';
import { ChybovostSvadlenPage } from '@/pages/ChybovostSvadlen';
import { CountdownsPage } from '@/pages/Countdowns';
import { DashboardPage } from '@/pages/Dashboard';
import { DatabazeKategoriiPage } from '@/pages/DatabazeKategorii';
import { DatabazeLatekPage } from '@/pages/DatabazeLatek';
import { DatabazeNapletuPage } from '@/pages/DatabazeNapletu';
import { DatabazeNitiPage } from '@/pages/DatabazeNiti';
import { DatabazeNoremPage } from '@/pages/DatabazeNorem';
import { DatabazeProduktuPage } from '@/pages/DatabazeProduktu';
import { DatabazeSvadlenekPage } from '@/pages/DatabazeSvadlenek';
import { DatabazeVyrobyPage } from '@/pages/DatabazeVyroby';
import { DatabazeZipuPage } from '@/pages/DatabazeZipu';
import { DelkaZakazekPage } from '@/pages/DelkaZakazek';
import { DokoncovaciDilnaPage } from '@/pages/DokoncovaciDilna';
import { DokumentacePage } from '@/pages/Dokumentace';
import { DruhaJakostPage } from '@/pages/DruhaJakost';
import { EshopyPage } from '@/pages/Eshopy';
import { ExterniVeraPage } from '@/pages/ExterniVera';
import { FormularePage } from '@/pages/Formulare';
import { InfoPage } from '@/pages/Info';
import { InformaceOLatcePage } from '@/pages/InformaceOLatce';
import { KontrolaLatekPage } from '@/pages/KontrolaLatek';
import { KontrolaNapletuPage } from '@/pages/KontrolaNapletu';
import { KontrolaNitiPage } from '@/pages/KontrolaNiti';
import { LinkGroupsListPage } from '@/pages/LinkGroupsList';
import { LogObjednavekPage } from '@/pages/LogObjednavek';
import { LoginPage } from '@/pages/Login';
import { NaZakazkuPage } from '@/pages/NaZakazku';
import { NaZakazkuLogPage } from '@/pages/NaZakazkuLog';
import { NakupPage } from '@/pages/Nakup';
import { NaskladneniZVyrobyPage } from '@/pages/NaskladneniZVyroby';
import { NovaObjednavkaPage } from '@/pages/NovaObjednavka';
import { OrderDatabasePage } from '@/pages/OrderDatabase';
import { ProductDatabasePage } from '@/pages/ProductDatabase';
import { ProductSetsListPage } from '@/pages/ProductSetsList';
import { ProduktTydnePage } from '@/pages/ProduktTydne';
import { RazeniKategoriiPage } from '@/pages/RazeniKategorii';
import { RecenzeSyncPage } from '@/pages/RecenzeSync';
import { RozdilySkladuPage } from '@/pages/RozdilySkladu';
import { SalesHistoryPage } from '@/pages/SalesHistory';
import { SiciDilnaPage } from '@/pages/SiciDilna';
import { SkladLatekPage } from '@/pages/SkladLatek';
import { SkladMaterialuPage } from '@/pages/SkladMaterialu';
import { SkladObjednavkyPage } from '@/pages/SkladObjednavky';
import { SkladPrijemPage } from '@/pages/SkladPrijem';
import { SkladoveZasobyPage } from '@/pages/SkladoveZasoby';
import { SnapshotDetailPage } from '@/pages/SnapshotDetail';
import { StatistikyPage } from '@/pages/Statistiky';
import { StriharnaPage } from '@/pages/Striharna';
import { TimelinePage } from '@/pages/Timeline';
import { ToolsLandingPage } from '@/pages/ToolsLanding';
import { TrideniNaZakazkuPage } from '@/pages/TrideniNaZakazku';
import { UpsellCrosssellPage } from '@/pages/UpsellCrosssell';
import { VykazTiskPage } from '@/pages/VykazTisk';
import { VykazyPage } from '@/pages/Vykazy';
import { VyrobaTerminyPage } from '@/pages/VyrobaTerminy';
import { VyskladneniPage } from '@/pages/Vyskladneni';
import { WatchdogPage } from '@/pages/Watchdog';
import { ZadaniDoVyrobyPage } from '@/pages/ZadaniDoVyroby';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

export function App() {
  const auth = useAuth();
  const { pathname } = useLocation();

  if (auth.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center text-text-muted text-sm">
        Načítám…
      </div>
    );
  }

  if (auth.status === 'anon') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage onLoggedIn={auth.refresh} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const { user, logout } = auth;
  // Route-level guarding podle modul accessu. Pokud uživatel nemá modul,
  // route se vůbec nedefinuje → fallback Navigate vrátí na landing.
  const mods = user.modules ?? [];

  // Na doméně vyroba.v-martsystem.cz je kořen rovnou modul Výroba, ne
  // rozcestník tools. Když uživatel modul nemá, spadne zpět na rozcestník —
  // jinak by se redirect zacyklil na nedefinované routě.
  const brand = detectBrand();
  // Kam vede kořen modulu — podle toho, na kterou část má uživatel právo.
  const vyrobaHome = hasModule(mods, 'vyroba_zakazky')
    ? '/vyroba/vyroba'
    : hasModule(mods, 'vyroba_plan_strihani')
      ? '/vyroba/striharna'
      : hasModule(mods, 'vyroba_sici')
        ? '/vyroba/sici-dilna'
        : hasModule(mods, 'vyroba_vykazy')
          ? '/vyroba/sici-dilna/vykazy'
          : hasModule(mods, 'vyroba_dilna_plan')
            ? '/vyroba/dokoncovaci-dilna'
            : hasModule(mods, 'vyroba_chybovost')
              ? '/vyroba/dokoncovaci-dilna/chybovost'
              : hasModule(mods, 'vyroba_sklad_latek')
                ? '/vyroba/sklad-latek'
                : // Účet externí dílny má jen tohle — ať po přihlášení skončí rovnou tam.
                  hasModule(mods, 'vyroba_externi_vera')
                  ? '/vyroba/externi/vera'
                  : '/informace';
  const homePath = brand.id === 'vyroba' && hasModule(mods, 'vyroba') ? '/vyroba' : '/';

  // Výkaz práce se otevírá v samostatném panelu — bez menu, ať vypadá jako
  // hotový dokument a dá se rovnou uložit jako PDF.
  if (pathname === '/vyroba/sici-dilna/vykazy/tisk' && hasModule(mods, 'vyroba')) {
    return <VykazTiskPage />;
  }

  return (
    <Layout user={user} onLogout={logout}>
      <Routes>
        {/* Landing */}
        <Route
          path="/"
          element={
            homePath === '/' ? <ToolsLandingPage user={user} /> : <Navigate to={homePath} replace />
          }
        />

        {/* Informace o aplikaci — dostupné všem přihlášeným */}
        <Route path="/informace" element={<InfoPage />} />

        {/* Sdílená data napříč tools */}
        {hasModule(mods, 'products_db') && (
          <Route path="/databaze-produktu" element={<ProductDatabasePage user={user} />} />
        )}
        {hasModule(mods, 'orders_db') && (
          <Route path="/databaze-objednavek" element={<OrderDatabasePage user={user} />} />
        )}

        {/* Tool: Propojení produktů */}
        {hasModule(mods, 'propojeni') && (
          <Route path="/propojeni-produktu" element={<LinkGroupsListPage user={user} />} />
        )}

        {/* Tool: Produktové sady */}
        {hasModule(mods, 'sady') && (
          <Route path="/produktove-sady" element={<ProductSetsListPage user={user} />} />
        )}

        {/* Tool: Analýza historie prodeje */}
        {hasModule(mods, 'sales_history') && (
          <Route path="/analyza-historie-prodeje" element={<SalesHistoryPage />} />
        )}

        {/* Tool: V-Mart Analytics (GA4) */}
        {hasModule(mods, 'analytics') && (
          <Route path="/analytics" element={<AnalyticsPage user={user} />} />
        )}

        {/* Tool: Odpočty času */}
        {hasModule(mods, 'odpocty') && (
          <Route path="/odpocty" element={<CountdownsPage user={user} />} />
        )}

        {/* Tool: Upsell & Cross-sell */}
        {hasModule(mods, 'upsell') && (
          <Route path="/upsell-cross-sell" element={<UpsellCrosssellPage user={user} />} />
        )}

        {/* Tool: Produkt týdne */}
        {hasModule(mods, 'produkt_tydne') && (
          <Route path="/produkt-tydne" element={<ProduktTydnePage user={user} />} />
        )}

        {/* Tool: Řazení v kategoriích */}
        {hasModule(mods, 'razeni_kategorii') && (
          <Route path="/razeni-kategorii" element={<RazeniKategoriiPage />} />
        )}

        {/* Tool: Skladové zásoby */}
        {hasModule(mods, 'skladove_zasoby') && (
          <Route path="/skladove-zasoby" element={<SkladoveZasobyPage />} />
        )}

        {/* Tool: Výroba (vlastní doména vyroba.v-martsystem.cz, stejný projekt) */}
        {hasModule(mods, 'vyroba') && (
          <>
            {/* Přehled nad production_orders zrušen (nikdy se nepoužil, 0 záznamů).
                Kořen modulu vede na zakázky z Tabidoo — na tuhle cestu míří
                domovská stránka domény i dlaždice na rozcestníku tools. */}
            <Route path="/vyroba" element={<Navigate to={vyrobaHome} replace />} />
            {hasModule(mods, 'user_admin') && (
              <Route path="/vyroba/dokumentace" element={<DokumentacePage />} />
            )}
            {hasModule(mods, 'vyroba_pozadavky') && (
              <Route path="/vyroba/nova-objednavka" element={<NovaObjednavkaPage user={user} />} />
            )}
            {hasModule(mods, 'vyroba_formulare') && (
              <Route path="/vyroba/formulare" element={<FormularePage />} />
            )}
            {hasModule(mods, 'vyroba_plan_strihani') && (
              <>
                <Route path="/vyroba/striharna" element={<StriharnaPage />} />
                <Route path="/vyroba/striharna/timeline" element={<TimelinePage />} />
              </>
            )}
            {hasModule(mods, 'vyroba_dilna_plan') && (
              <Route path="/vyroba/dokoncovaci-dilna" element={<DokoncovaciDilnaPage />} />
            )}
            {hasModule(mods, 'vyroba_chybovost') && (
              <Route
                path="/vyroba/dokoncovaci-dilna/chybovost"
                element={<ChybovostSvadlenPage />}
              />
            )}
            {hasModule(mods, 'vyroba_sici') && (
              <Route path="/vyroba/sici-dilna" element={<SiciDilnaPage />} />
            )}
            {hasModule(mods, 'vyroba_vykazy') && (
              <Route path="/vyroba/sici-dilna/vykazy" element={<VykazyPage />} />
            )}
            {hasModule(mods, 'vyroba_na_zakazku') && (
              <>
                <Route path="/vyroba/striharna/na-zakazku" element={<NaZakazkuPage />} />
                <Route path="/vyroba/striharna/na-zakazku/log" element={<NaZakazkuLogPage />} />
              </>
            )}
            {hasModule(mods, 'vyroba_vyskladneni') && (
              <Route path="/vyroba/striharna/vyskladneni" element={<VyskladneniPage />} />
            )}
            {hasModule(mods, 'vyroba_info_latka') && (
              <Route path="/vyroba/striharna/latka" element={<InformaceOLatcePage />} />
            )}
            {hasModule(mods, 'vyroba_druha_jakost') && (
              <Route path="/vyroba/druha-jakost" element={<DruhaJakostPage />} />
            )}
            {hasModule(mods, 'vyroba_kontrola_napletu') && (
              <Route path="/vyroba/striharna/naplet" element={<KontrolaNapletuPage />} />
            )}
            {hasModule(mods, 'vyroba_kontrola_latek') && (
              <Route path="/vyroba/striharna/latky" element={<KontrolaLatekPage />} />
            )}
            {hasModule(mods, 'vyroba_kontrola_niti') && (
              <Route path="/vyroba/striharna/nite" element={<KontrolaNitiPage />} />
            )}
            {hasModule(mods, 'vyroba_delka_zakazek') && (
              <Route path="/vyroba/striharna/delka-zakazek" element={<DelkaZakazekPage />} />
            )}
            {hasModule(mods, 'vyroba_sklad_latek') && (
              <Route path="/vyroba/sklad-latek" element={<SkladLatekPage />} />
            )}
            {hasModule(mods, 'vyroba_prijem') && (
              <Route path="/vyroba/sklad-latek/prijem" element={<SkladPrijemPage />} />
            )}
            {hasModule(mods, 'vyroba_objednavky') && (
              <Route path="/vyroba/sklad-latek/objednavky" element={<SkladObjednavkyPage />} />
            )}
            {hasModule(mods, 'vyroba_sklad_materialu') && (
              <Route path="/vyroba/nakup/material" element={<SkladMaterialuPage />} />
            )}
            {hasModule(mods, 'vyroba_nakup') && (
              <Route path="/vyroba/nakup" element={<NakupPage />} />
            )}
            {hasModule(mods, 'vyroba_externi_vera') && (
              <Route path="/vyroba/externi/vera" element={<ExterniVeraPage />} />
            )}
            {hasModule(mods, 'vyroba_zakazky') && (
              <Route path="/vyroba/vyroba" element={<DatabazeVyrobyPage />} />
            )}
            {hasModule(mods, 'vyroba_terminy') && (
              <Route path="/vyroba/terminy" element={<VyrobaTerminyPage />} />
            )}
            {hasModule(mods, 'vyroba_zadani') && (
              <Route path="/vyroba/zadani" element={<ZadaniDoVyrobyPage />} />
            )}
            {hasModule(mods, 'vyroba_statistiky') && (
              <Route path="/vyroba/statistiky" element={<StatistikyPage />} />
            )}
            {hasModule(mods, 'vyroba_trideni_na_zakazku') && (
              <Route path="/vyroba/trideni-na-zakazku" element={<TrideniNaZakazkuPage />} />
            )}
            {hasModule(mods, 'vyroba_log_objednavek') && (
              <Route path="/vyroba/log-objednavek" element={<LogObjednavekPage />} />
            )}
            {hasModule(mods, 'vyroba_automatizace') && (
              <Route path="/vyroba/automatizace" element={<AutomatizacePage />} />
            )}
            {hasModule(mods, 'vyroba_naskladneni') && (
              <Route path="/vyroba/naskladneni-z-vyroby" element={<NaskladneniZVyrobyPage />} />
            )}
            {hasModule(mods, 'vyroba_eshopy') && (
              <Route path="/vyroba/eshopy" element={<EshopyPage />} />
            )}
            {hasModule(mods, 'vyroba_zdroje_kategorie') && (
              <Route path="/vyroba/zdroje/kategorie" element={<DatabazeKategoriiPage />} />
            )}
            {hasModule(mods, 'vyroba_zdroje_niti') && (
              <Route path="/vyroba/zdroje/niti" element={<DatabazeNitiPage />} />
            )}
            {hasModule(mods, 'vyroba_zdroje_zipy') && (
              <Route path="/vyroba/zdroje/zipy" element={<DatabazeZipuPage />} />
            )}
            {hasModule(mods, 'vyroba_zdroje_latky') && (
              <Route path="/vyroba/zdroje/latky" element={<DatabazeLatekPage />} />
            )}
            {hasModule(mods, 'vyroba_zdroje_produkty') && (
              <Route path="/vyroba/zdroje/produkty" element={<DatabazeProduktuPage />} />
            )}
            {hasModule(mods, 'vyroba_zdroje_napletu') && (
              <Route path="/vyroba/zdroje/napletu" element={<DatabazeNapletuPage />} />
            )}
            {hasModule(mods, 'vyroba_zdroje_svadlenky') && (
              <Route path="/vyroba/zdroje/svadlenky" element={<DatabazeSvadlenekPage />} />
            )}
            {hasModule(mods, 'vyroba_zdroje_normy') && (
              <Route path="/vyroba/zdroje/normy" element={<DatabazeNoremPage />} />
            )}
          </>
        )}

        {/* Tool: Hlídací pes */}
        {hasModule(mods, 'watchdog') && (
          <Route path="/hlidaci-pes" element={<WatchdogPage user={user} />} />
        )}

        {/* Tool: Automatizace objednávek */}
        {hasModule(mods, 'automatizace_objednavek') && (
          <Route path="/automatizace-objednavek" element={<AutomatizaceObjednavekPage />} />
        )}

        {/* Tool: Rozdíly skladů */}
        {hasModule(mods, 'rozdily_skladu') && (
          <Route path="/rozdily-skladu" element={<RozdilySkladuPage />} />
        )}

        {/* Tool: Sync recenzí CZ ↔ SK */}
        {hasModule(mods, 'recenze_sync') && (
          <Route path="/recenze-sync" element={<RecenzeSyncPage />} />
        )}

        {/* Tool: Sklad-Sync */}
        {hasModule(mods, 'sklad_sync') && (
          <>
            <Route path="/sklad-sync" element={<DashboardPage user={user} />} />
            <Route path="/sklad-sync/snapshot/:id" element={<SnapshotDetailPage />} />
            <Route path="/sklad-sync/admin/recipients" element={<AdminRecipientsPage />} />
            <Route path="/sklad-sync/admin/excluded" element={<AdminExcludedPage />} />
          </>
        )}

        {/* Super-admin: správa uživatelů */}
        {hasModule(mods, 'user_admin') && (
          <Route path="/admin/users" element={<AdminUsersPage />} />
        )}

        <Route path="/login" element={<Navigate to={homePath} replace />} />
        <Route path="*" element={<Navigate to={homePath} replace />} />
      </Routes>
    </Layout>
  );
}
